import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PageLoader } from "@/components/PageLoader";
import { AppLayout } from "@/components/AppLayout";

// Critical routes - load immediately
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy load non-critical routes for code splitting
const Admin = lazy(() => import("./pages/Admin"));
const Appointments = lazy(() => import("./pages/Appointments"));
const Configurator = lazy(() => import("./pages/Configurator"));
const Quotes = lazy(() => import("./pages/Quotes"));
const Presentation = lazy(() => import("./pages/Presentation"));
const SalesPresentation = lazy(() => import("./pages/SalesPresentation"));
const Contacts = lazy(() => import("./pages/Contacts"));
const PlanComparison = lazy(() => import("./pages/PlanComparison"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const Profile = lazy(() => import("./pages/Profile"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));

// Configure QueryClient with optimized caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - cache garbage collection (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Only retry once on failure
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppLayout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin/*" element={<Admin />} />
                <Route path="/appointments" element={<Appointments />} />
                <Route path="/configurator" element={<Configurator />} />
                <Route path="/quotes" element={<Quotes />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/presentation" element={<Presentation />} />
                <Route path="/sales-presentation" element={<SalesPresentation />} />
                <Route path="/compare-plans" element={<PlanComparison />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/pay" element={<PaymentPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AppLayout>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
